import { Avatar, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Popover, Tooltip } from 'antd'
import { formatDisplayName } from 'screens/Rostering/Reconciliation/reconciliationUtils'
import DeactivatedIcon from 'screens/Rostering/RotaBuilder/common/components/icons/DeactivatedIcon'
import DeletedIcon from 'screens/Rostering/RotaBuilder/common/components/icons/DeletedIcon'
import styled from 'styled-components'
import type { PayReportRow } from '../types/payReport'
import { PayRateContractPopoverBody } from './PayRateContractPopoverBody'
import {
  PAY_REPORT_DELETED_TM_SUPPORT_URL,
  resolvePayReportTeamMemberStatus
} from './payReportTeamMemberStatus'

const TeamMemberHoverArea = styled.span`
  display: inline-flex;
  width: 100%;
  min-width: 0;
  cursor: default;
`

const StatusIconWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  width: 14px;
  height: 14px;
`

const TooltipTitleText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color11};
`

const UnderlinedLink = styled.a`
  text-decoration: underline;
  color: ${({ theme }) => theme.color.status.primary};
`

const PersonCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const NameText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

type Translate = (key: string, options?: Record<string, unknown>) => string

type PayReportTeamMemberCellProps = {
  record: PayReportRow
  t: Translate
  resolveContractTypeLabel: (contractTypeId?: string | null) => string
}

export const PayReportTeamMemberCell = ({
  record,
  t,
  resolveContractTypeLabel
}: PayReportTeamMemberCellProps) => {
  const status = resolvePayReportTeamMemberStatus(record)
  return (
    <Popover
      trigger="hover"
      placement="top"
      showArrow
      arrow={{ pointAtCenter: true }}
      mouseEnterDelay={0.2}
      content={
        <PayRateContractPopoverBody
          record={record}
          t={t}
          resolveContractTypeLabel={resolveContractTypeLabel}
        />
      }
    >
      <TeamMemberHoverArea>
        <PersonCell>
          <Avatar url={record.teamMember.picture} size={22} />
          <NameText weight="medium">
            {formatDisplayName(record.teamMember.name)}
          </NameText>
          {status === 'deleted' ? (
            <Tooltip
              color={theme.color.neutral.color3}
              placement="bottom"
              title={
                <TooltipTitleText>
                  {t('TM_DELETED_TOOLTIP')}{' '}
                  <UnderlinedLink
                    href={PAY_REPORT_DELETED_TM_SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('HERE')}
                  </UnderlinedLink>
                  .
                </TooltipTitleText>
              }
            >
              <StatusIconWrap>
                <DeletedIcon />
              </StatusIconWrap>
            </Tooltip>
          ) : status === 'inactive' ? (
            <Tooltip
              color={theme.color.neutral.color3}
              placement="bottom"
              title={
                <TooltipTitleText>
                  {t('TM_DEACTIVATED_TOOLTIP')}
                </TooltipTitleText>
              }
            >
              <StatusIconWrap>
                <DeactivatedIcon />
              </StatusIconWrap>
            </Tooltip>
          ) : null}
        </PersonCell>
      </TeamMemberHoverArea>
    </Popover>
  )
}
